import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export default function OrderSettings() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [tab, setTab] = useState('general');

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <h2 style={{ margin: '0 0 20px', fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>⚙️ Order Settings</h2>
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {[['general', 'General Settings'], ['statuses', 'Order Statuses'], ['audit', 'Audit Log']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            style={{ padding: '8px 20px', borderRadius: 8, border: tab === key ? '2px solid #3b82f6' : '1px solid #d1d5db',
              background: tab === key ? '#eff6ff' : '#fff', color: tab === key ? '#3b82f6' : '#374151',
              fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            {label}
          </button>
        ))}
      </div>
      {tab === 'general' && <GeneralSettings companyId={companyId} />}
      {tab === 'statuses' && <StatusManager companyId={companyId} />}
      {tab === 'audit' && <AuditLog companyId={companyId} />}
    </div>
  );
}

// ═══════════════════════════════
// ── General Settings ──
// ═══════════════════════════════
function GeneralSettings({ companyId }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const fetchSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/orders/settings`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed');
      setSettings(await res.json());
    } catch (e) { console.error(e); }
  }, [companyId]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true); setMsg('');
    try {
      const res = await fetch(`${API}/companies/${companyId}/orders/settings`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
      });
      if (!res.ok) throw new Error('Failed');
      setSettings(await res.json());
      setMsg('Settings saved ✓');
      setTimeout(() => setMsg(''), 3000);
    } catch (e) { setMsg('Error saving'); }
    setSaving(false);
  };

  if (!settings) return <div>Loading...</div>;

  const update = (field, val) => setSettings({ ...settings, [field]: val });

  return (
    <div style={{ maxWidth: 700 }}>
      {/* Defaults */}
      <Section title="Defaults">
        <Row label="Default Currency">
          <select value={settings.default_currency} onChange={e => update('default_currency', e.target.value)} style={inputStyle}>
            {['USD','LBP','EUR','GBP','AED','SAR'].map(c => <option key={c}>{c}</option>)}
          </select>
        </Row>
        <Row label="Department Rule">
          <select value={settings.department_rule} onChange={e => update('department_rule', e.target.value)} style={inputStyle}>
            <option value="optional">Optional</option><option value="required">Required</option>
          </select>
        </Row>
        <Row label="Expected Delivery Date">
          <select value={settings.expected_delivery_rule} onChange={e => update('expected_delivery_rule', e.target.value)} style={inputStyle}>
            <option value="optional">Optional</option><option value="required_for_products">Required for Products</option>
          </select>
        </Row>
        <Row label="Client PO">
          <select value={settings.client_po_rule} onChange={e => update('client_po_rule', e.target.value)} style={inputStyle}>
            <option value="optional">Optional</option><option value="required">Required</option><option value="not_used">Not Used</option>
          </select>
        </Row>
      </Section>

      {/* Products & Services */}
      <Section title="Products & Services">
        <Row label="New Product Creation">
          <select value={settings.new_product_creation} onChange={e => update('new_product_creation', e.target.value)} style={inputStyle}>
            <option value="allow">Allow</option><option value="management_only">Management Only</option><option value="not_allowed">Not Allowed</option>
          </select>
        </Row>
        <Row label="New Service Creation">
          <select value={settings.new_service_creation} onChange={e => update('new_service_creation', e.target.value)} style={inputStyle}>
            <option value="allow">Allow</option><option value="management_only">Management Only</option><option value="not_allowed">Not Allowed</option>
          </select>
        </Row>
        <Row label="Allow Free Description Lines">
          <Toggle checked={settings.allow_free_description} onChange={v => update('allow_free_description', v)} />
        </Row>
      </Section>

      {/* Fulfillment */}
      <Section title="Fulfillment">
        <Row label="Delivery Proof Required">
          <Toggle checked={settings.delivery_proof_required} onChange={v => update('delivery_proof_required', v)} />
        </Row>
        <Row label="Insufficient Stock Rule">
          <select value={settings.insufficient_stock_rule} onChange={e => update('insufficient_stock_rule', e.target.value)} style={inputStyle}>
            <option value="allow_with_warning">Allow with Warning</option><option value="block">Block Confirmation</option>
          </select>
        </Row>
        <Row label="Allow Invoice from Order">
          <Toggle checked={settings.allow_invoice_from_order} onChange={v => update('allow_invoice_from_order', v)} />
        </Row>
      </Section>

      {/* Numbering */}
      <Section title="Numbering">
        <Row label="Order Prefix">
          <input value={settings.order_prefix || ''} onChange={e => update('order_prefix', e.target.value)} style={inputStyle} />
        </Row>
        <Row label="Delivery Prefix">
          <input value={settings.delivery_prefix || ''} onChange={e => update('delivery_prefix', e.target.value)} style={inputStyle} />
        </Row>
        <Row label="Delivery Due Soon (days)">
          <input type="number" value={settings.delivery_due_soon_days ?? ''} onChange={e => update('delivery_due_soon_days', Number(e.target.value))} style={inputStyle} />
        </Row>
      </Section>

      {/* Notifications */}
      <Section title="Notifications">
        {[
          ['notify_order_confirmed', 'Order Confirmed'],
          ['notify_reservation_failed', 'Reservation Failed'],
          ['notify_insufficient_stock', 'Insufficient Stock'],
          ['notify_delivery_due_soon', 'Delivery Due Soon'],
          ['notify_delivery_overdue', 'Delivery Overdue'],
          ['notify_partial_delivery', 'Partial Delivery'],
          ['notify_full_delivery', 'Full Delivery'],
          ['notify_order_completed', 'Order Completed'],
          ['notify_installment_due', 'Installment Due'],
          ['notify_invoice_created', 'Invoice Created'],
          ['notify_payment_received', 'Payment Received'],
        ].map(([field, label]) => (
          <Row key={field} label={label}>
            <Toggle checked={settings[field]} onChange={v => update(field, v)} />
          </Row>
        ))}
      </Section>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 20 }}>
        <button onClick={handleSave} disabled={saving} style={btnPrimary}>{saving ? 'Saving...' : 'Save Settings'}</button>
        {msg && <span style={{ fontSize: 13, color: msg.includes('✓') ? '#10b981' : '#ef4444' }}>{msg}</span>}
      </div>
    </div>
  );
}

// ═══════════════════════════════
// ── Status Manager ──
// ═══════════════════════════════
function StatusManager({ companyId }) {
  const [statuses, setStatuses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', related_system_state: 'draft', is_active: true, notes: '' });
  const [saving, setSaving] = useState(false);

  const fetchStatuses = useCallback(async () => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/orders/statuses`, { headers: getAuthHeaders() });
      if (!res.ok) return;
      const json = await res.json();
      setStatuses(Array.isArray(json) ? json : []);
    } catch (e) {}
  }, [companyId]);

  useEffect(() => { fetchStatuses(); }, [fetchStatuses]);

  const handleSave = async () => {
    if (!form.name.trim()) return alert('Name is required');
    setSaving(true);
    try {
      const isEdit = !!editing;
      const url = isEdit ? `${API}/companies/${companyId}/orders/statuses/${editing.id}` : `${API}/companies/${companyId}/orders/statuses`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) { const err = await res.json(); alert(err.message || 'Error'); setSaving(false); return; }
      setShowForm(false); setEditing(null); setForm({ name: '', related_system_state: 'draft', is_active: true, notes: '' });
      fetchStatuses();
    } catch (e) { alert('Error'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this status?')) return;
    try {
      const res = await fetch(`${API}/companies/${companyId}/orders/statuses/${id}`, {
        method: 'DELETE', headers: getAuthHeaders(),
      });
      if (!res.ok) { const err = await res.json(); alert(err.message); return; }
      fetchStatuses();
    } catch (e) { alert('Error'); }
  };

  const openEdit = (s) => {
    setForm({ name: s.name, related_system_state: s.related_system_state, is_active: s.is_active, notes: s.notes || '' });
    setEditing(s);
    setShowForm(true);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>
            Protected system states (Draft, Confirmed, Completed, Cancelled) cannot be modified.
            Create custom operational statuses below.
          </p>
        </div>
        <button onClick={() => { setForm({ name: '', related_system_state: 'draft', is_active: true, notes: '' }); setEditing(null); setShowForm(true); }}
          style={btnPrimary}>+ Add Status</button>
      </div>

      {/* Protected States */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        {[['Draft', '📝', '#9ca3af'], ['Confirmed', '✅', '#3b82f6'], ['Completed', '🎉', '#10b981'], ['Cancelled', '❌', '#ef4444']].map(([name, icon, color]) => (
          <div key={name} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 20 }}>{icon}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color }}>{name}</div>
            <div style={{ fontSize: 11, color: '#9ca3af' }}>System State (Protected)</div>
          </div>
        ))}
      </div>

      {/* Custom Statuses Table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>{['Name','Related State','Display Order','Active','Orders','Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {statuses.map(s => (
            <tr key={s.id}>
              <td style={{ ...tdStyle, fontWeight: 600 }}>{s.name}</td>
              <td style={tdStyle}><Badge value={s.related_system_state} /></td>
              <td style={{ ...tdStyle, textAlign: 'center' }}>{s.display_order}</td>
              <td style={tdStyle}>{s.is_active ? <span style={{ color: '#10b981' }}>✓ Active</span> : <span style={{ color: '#9ca3af' }}>Inactive</span>}</td>
              <td style={{ ...tdStyle, textAlign: 'center' }}>{s.orders_count ?? 0}</td>
              <td style={tdStyle}>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={() => openEdit(s)} style={btnSm}>Edit</button>
                  <button onClick={() => handleDelete(s.id)} style={{ ...btnSm, color: '#ef4444' }}>Delete</button>
                </div>
              </td>
            </tr>
          ))}
          {statuses.length === 0 && <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No custom statuses created yet</td></tr>}
        </tbody>
      </table>

      {/* Form Modal */}
      {showForm && (
        <Modal title={editing ? 'Edit Status' : 'New Status'} onClose={() => { setShowForm(false); setEditing(null); }}>
          <label style={labelStyle}>Status Name *
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={inputStyle} />
          </label>
          <label style={{ ...labelStyle, marginTop: 12 }}>Related System State
            <select value={form.related_system_state} onChange={e => setForm({ ...form, related_system_state: e.target.value })} style={inputStyle}>
              <option value="draft">Draft</option><option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option><option value="cancelled">Cancelled</option>
            </select>
          </label>
          <label style={{ ...labelStyle, marginTop: 12 }}>Notes
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} style={{ ...inputStyle, minHeight: 60 }} />
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginTop: 12 }}>
            <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} />
            Active
          </label>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <button onClick={() => { setShowForm(false); setEditing(null); }} style={btnSm}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={btnPrimary}>{saving ? 'Saving...' : editing ? 'Save' : 'Create'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════
// ── Audit Log ──
// ═══════════════════════════════
function AuditLog({ companyId }) {
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1 });
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const fetchLogs = useCallback(async () => {
    try {
      let url = `${API}/companies/${companyId}/orders/audit?page=${page}`;
      if (actionFilter) url += `&action=${actionFilter}`;
      if (typeFilter) url += `&auditable_type=${typeFilter}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) return;
      const json = await res.json();
      setLogs(Array.isArray(json.data) ? json.data : []);
      setPagination({ current_page: json.current_page, last_page: json.last_page });
    } catch (e) {}
  }, [companyId, page, actionFilter, typeFilter]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <select value={actionFilter} onChange={e => { setActionFilter(e.target.value); setPage(1); }} style={selectStyle}>
          <option value="">All Actions</option>
          {['created','confirmed','completed','cancelled','revised','updated','status_changed','delivery_created','delivery_confirmed','delivery_reversed',
            'auto_completed','status_created','status_updated','status_deleted','settings_updated'].map(a =>
            <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>)}
        </select>
        <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }} style={selectStyle}>
          <option value="">All Types</option>
          <option value="OrdOrder">Order</option><option value="OrdDelivery">Delivery</option>
          <option value="OrdStatus">Status</option><option value="OrdSetting">Setting</option>
        </select>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>{['Date','Action','Type','ID','Old Value','New Value','User','Reason'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {logs.map(l => (
            <tr key={l.id}>
              <td style={tdStyle}>{l.created_at?.replace('T', ' ').substring(0, 19)}</td>
              <td style={tdStyle}><Badge value={l.action} /></td>
              <td style={tdStyle}>{l.auditable_type}</td>
              <td style={{ ...tdStyle, textAlign: 'center' }}>{l.auditable_id}</td>
              <td style={{ ...tdStyle, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.old_value || '—'}</td>
              <td style={{ ...tdStyle, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.new_value || '—'}</td>
              <td style={tdStyle}>{l.user?.name || '—'}</td>
              <td style={tdStyle}>{l.reason || '—'}</td>
            </tr>
          ))}
          {logs.length === 0 && <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No audit logs</td></tr>}
        </tbody>
      </table>

      {pagination.last_page > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} style={btnSm}>← Prev</button>
          <span style={{ padding: '6px 12px', fontSize: 13 }}>Page {pagination.current_page} of {pagination.last_page}</span>
          <button disabled={page >= pagination.last_page} onClick={() => setPage(page + 1)} style={btnSm}>Next →</button>
        </div>
      )}
    </div>
  );
}

// ── Shared Components ──

function Modal({ title, children, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 500,
        maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}
        onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700, color: '#1a1a2e' }}>{title}</h3>
        {children}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <h3 style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #e5e7eb' }}>{title}</h3>
      {children}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
      <span style={{ fontSize: 13, color: '#374151' }}>{label}</span>
      <div style={{ width: 240 }}>{children}</div>
    </div>
  );
}

function Toggle({ checked, onChange }) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
        style={{ width: 18, height: 18, accentColor: '#3b82f6' }} />
    </label>
  );
}

function Badge({ value }) {
  const colors = {
    draft: '#9ca3af', confirmed: '#3b82f6', completed: '#10b981', cancelled: '#ef4444',
    created: '#10b981', updated: '#3b82f6', revised: '#f59e0b', status_changed: '#8b5cf6',
    delivery_created: '#3b82f6', delivery_confirmed: '#10b981', delivery_reversed: '#ef4444',
    auto_completed: '#10b981', status_created: '#10b981', status_updated: '#3b82f6',
    status_deleted: '#ef4444', settings_updated: '#8b5cf6',
  };
  const c = colors[value] || '#6b7280';
  return <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: c + '18', color: c }}>{(value || '').replace(/_/g, ' ')}</span>;
}

const btnPrimary = { padding: '8px 20px', borderRadius: 8, border: 'none', background: '#3b82f6', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnSm = { padding: '4px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', fontSize: 12, cursor: 'pointer' };
const inputStyle = { padding: '7px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, width: '100%', boxSizing: 'border-box' };
const selectStyle = { padding: '7px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, background: '#fff' };
const labelStyle = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 500, color: '#374151' };
const thStyle = { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e5e7eb', fontSize: 12, fontWeight: 600, color: '#6b7280', whiteSpace: 'nowrap' };
const tdStyle = { padding: '10px 12px', borderBottom: '1px solid #f3f4f6', color: '#374151', whiteSpace: 'nowrap' };
